import React, { useRef, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  useFetchPageSummariesQuery,
  useFetchPageImagesQuery,
  useUploadPageImageMutation,
  useUpdatePageImageMutation,
  useDeletePageImageMutation,
  useReorderPageImagesMutation,
  type PageName,
  type ProjectImageRecord,
} from "../../store/api/projectImagesApi";
import "./admin.css";

// ── Config ──────────────────────────────────────────────────────────────────
const PAGES: { name: PageName; label: string; icon: string; accent: string; desc: string; group: string }[] = [
  { name: "Residential", label: "Residential",  icon: "bi-house-heart-fill",    accent: "#0d9488", desc: "Shown on the Residential Projects page",                               group: "Projects" },
  { name: "Commercial",  label: "Commercial",   icon: "bi-buildings-fill",      accent: "#6366f1", desc: "Shown on the Commercial Projects page",                                group: "Projects" },
  { name: "Industrial",  label: "Industrial",   icon: "bi-gear-fill",           accent: "#f59e0b", desc: "Shown on the Industrial Projects page",                                group: "Projects" },
  { name: "General",     label: "All Projects", icon: "bi-grid-fill",           accent: "#0ea5e9", desc: "Featured images for the All Projects page",                            group: "Projects" },
  { name: "Cement",      label: "Cement",       icon: "bi-bag-fill",            accent: "#78716c", desc: "Real product photos shown in the Materials section on the Home page",  group: "Materials" },
  { name: "Steel",       label: "Steel Bar",    icon: "bi-dash-lg",             accent: "#64748b", desc: "Real product photos shown in the Materials section on the Home page",  group: "Materials" },
  { name: "Paint",       label: "Paint",        icon: "bi-brush-fill",          accent: "#ec4899", desc: "Real product photos shown in the Materials section on the Home page",  group: "Materials" },
  { name: "Tiles",       label: "Tiles",        icon: "bi-grid-3x3-gap-fill",   accent: "#8b5cf6", desc: "Real product photos shown in the Materials section on the Home page",  group: "Materials" },
];

// ── Page tab (loads images for one page) ────────────────────────────────────
const PageTab: React.FC<{ page: PageName; accent: string }> = ({ page, accent }) => {
  const { data: images = [], isLoading, isFetching } = useFetchPageImagesQuery(page);
  const [upload, { isLoading: isUploading }]    = useUploadPageImageMutation();
  const [remove, { isLoading: isDeleting }]     = useDeletePageImageMutation();
  const [update, { isLoading: isUpdating }]     = useUpdatePageImageMutation();
  const [reorder]                               = useReorderPageImagesMutation();

  const fileRef = useRef<HTMLInputElement>(null);
  const [err, setErr]         = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editId, setEditId]   = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc]   = useState("");

  const flash = (ok: boolean, msg: string) => {
    if (ok) { setSuccess(msg); setErr(null); }
    else     { setErr(msg);   setSuccess(null); }
    setTimeout(() => { setSuccess(null); setErr(null); }, 3500);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    try {
      await upload({ page, file, title: file.name.replace(/\.[^.]+$/, "") }).unwrap();
      flash(true, "Image uploaded successfully.");
    } catch (ex) {
      flash(false, getApiErrorMessage(ex as FetchBaseQueryError));
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDelete = async (img: ProjectImageRecord) => {
    if (!window.confirm(`Delete "${img.title}"? This cannot be undone.`)) return;
    try {
      await remove({ page, id: Number(img.id) }).unwrap();
      flash(true, "Image deleted.");
    } catch (ex) {
      flash(false, getApiErrorMessage(ex as FetchBaseQueryError));
    }
  };

  const startEdit = (img: ProjectImageRecord) => {
    setEditId(Number(img.id));
    setEditTitle(img.title);
    setEditDesc(img.description ?? "");
  };

  const handleSaveEdit = async () => {
    if (editId == null) return;
    const img = images.find((i) => Number(i.id) === editId);
    try {
      await update({
        page,
        id: editId,
        title: editTitle,
        description: editDesc,
        isFeatured: (img as any)?.isFeatured ?? false,
        sortOrder:  (img as any)?.sortOrder  ?? 0,
      }).unwrap();
      flash(true, "Saved.");
      setEditId(null);
    } catch (ex) {
      flash(false, getApiErrorMessage(ex as FetchBaseQueryError));
    }
  };

  const moveImage = async (idx: number, dir: -1 | 1) => {
    const swapped = [...images];
    const target  = idx + dir;
    if (target < 0 || target >= swapped.length) return;
    [swapped[idx], swapped[target]] = [swapped[target], swapped[idx]];
    const items = swapped.map((img, i) => ({ id: Number(img.id), sortOrder: i }));
    try { await reorder({ page, items }).unwrap(); }
    catch { /* silent — UI still shows optimistic order */ }
  };

  const toggleFeatured = async (img: ProjectImageRecord) => {
    try {
      await update({
        page,
        id: Number(img.id),
        title: img.title,
        description: img.description,
        isFeatured: !(img as any).isFeatured,
        sortOrder:  (img as any).sortOrder ?? 0,
      }).unwrap();
    } catch (ex) {
      flash(false, getApiErrorMessage(ex as FetchBaseQueryError));
    }
  };

  return (
    <div>
      {/* Alerts */}
      {err && (
        <div style={{ marginBottom: 12, padding: "9px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fca5a5", display: "flex", alignItems: "center", gap: 8 }}>
          <i className="bi bi-x-circle-fill" style={{ color: "#dc2626" }} />
          <span style={{ fontSize: "0.84rem", color: "#dc2626" }}>{err}</span>
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 12, padding: "9px 14px", borderRadius: 8, background: "#f0fdf4", border: "1px solid #86efac", display: "flex", alignItems: "center", gap: 8 }}>
          <i className="bi bi-check-circle-fill" style={{ color: "#16a34a" }} />
          <span style={{ fontSize: "0.84rem", color: "#15803d" }}>{success}</span>
        </div>
      )}

      {/* Upload bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, padding: "12px 16px", background: "#f8fafc", border: "1.5px dashed #cbd5e1", borderRadius: 10 }}>
        <i className="bi bi-cloud-upload-fill" style={{ color: accent, fontSize: "1.25rem" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: "0.84rem", color: "#334155" }}>Upload new image</div>
          <div style={{ fontSize: "0.7rem", color: "#94a3b8" }}>JPG / PNG · max 5 MB · images are served immediately on the website</div>
        </div>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png" title="Upload image" aria-label="Upload image file" style={{ display: "none" }} onChange={handleUpload} />
        <button
          type="button"
          disabled={isUploading}
          onClick={() => fileRef.current?.click()}
          style={{ background: isUploading ? "#e2e8f0" : accent, color: "#fff", border: "none", borderRadius: 8, padding: "7px 18px", fontWeight: 700, fontSize: "0.82rem", cursor: isUploading ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {isUploading
            ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Uploading…</>
            : <><i className="bi bi-plus-lg" /> Add Image</>}
        </button>
      </div>

      {/* Images grid */}
      {isLoading || isFetching ? (
        <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          Loading images…
        </div>
      ) : images.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
          <i className="bi bi-images" style={{ fontSize: "2rem", display: "block", marginBottom: 10 }} />
          <p style={{ marginBottom: 0, fontSize: "0.88rem" }}>No images yet. Upload the first one above.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
          {images.map((img, idx) => (
            <div key={img.id} style={{ background: "#fff", border: `1.5px solid ${editId === Number(img.id) ? accent : "#e2e8f0"}`, borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 6px #0000000a" }}>
              {/* Image */}
              <div style={{ position: "relative", aspectRatio: "4/3", background: "#f1f5f9" }}>
                <img
                  src={img.imageUrl}
                  alt={img.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
                />
                {/* Featured badge */}
                {(img as any).isFeatured && (
                  <span style={{ position: "absolute", top: 7, left: 7, background: "#fbbf24", color: "#78350f", fontSize: "0.62rem", fontWeight: 700, padding: "2px 7px", borderRadius: 20 }}>
                    <i className="bi bi-star-fill me-1" />Featured
                  </span>
                )}
                {/* Sort order badge */}
                <span style={{ position: "absolute", top: 7, right: 7, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: "0.62rem", fontWeight: 700, padding: "2px 7px", borderRadius: 20 }}>
                  #{idx + 1}
                </span>
              </div>

              {/* Inline edit or info */}
              {editId === Number(img.id) ? (
                <div style={{ padding: "10px 12px" }}>
                  <input
                    className="form-control form-control-sm mb-1"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Title"
                  />
                  <input
                    className="form-control form-control-sm mb-2"
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Description (optional)"
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      disabled={isUpdating}
                      onClick={handleSaveEdit}
                      style={{ flex: 1, background: accent, color: "#fff", border: "none", borderRadius: 6, padding: "5px 0", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}
                    >
                      {isUpdating ? "…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditId(null)}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 0", fontSize: "0.78rem", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: "8px 12px" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.8rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
                    {img.title || "Untitled"}
                  </div>
                  {img.description && (
                    <div style={{ fontSize: "0.7rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 6 }}>
                      {img.description}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {/* Move up */}
                    <button type="button" title="Move up" disabled={idx === 0} onClick={() => moveImage(idx, -1)}
                      style={btnStyle("#f8fafc", "#475569")}>
                      <i className="bi bi-arrow-up" />
                    </button>
                    {/* Move down */}
                    <button type="button" title="Move down" disabled={idx === images.length - 1} onClick={() => moveImage(idx, 1)}
                      style={btnStyle("#f8fafc", "#475569")}>
                      <i className="bi bi-arrow-down" />
                    </button>
                    {/* Edit */}
                    <button type="button" title="Edit" onClick={() => startEdit(img)}
                      style={btnStyle("#eff6ff", "#1d4ed8")}>
                      <i className="bi bi-pencil-fill" />
                    </button>
                    {/* Featured toggle */}
                    <button type="button" title={(img as any).isFeatured ? "Unmark featured" : "Mark as featured"} onClick={() => toggleFeatured(img)}
                      style={btnStyle((img as any).isFeatured ? "#fefce8" : "#f8fafc", (img as any).isFeatured ? "#d97706" : "#94a3b8")}>
                      <i className={(img as any).isFeatured ? "bi bi-star-fill" : "bi bi-star"} />
                    </button>
                    {/* Delete */}
                    <button type="button" title="Delete" disabled={isDeleting} onClick={() => handleDelete(img)}
                      style={btnStyle("#fef2f2", "#dc2626")}>
                      <i className="bi bi-trash3-fill" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const btnStyle = (bg: string, color: string): React.CSSProperties => ({
  background: bg,
  color,
  border: `1px solid ${bg === "#f8fafc" ? "#e2e8f0" : bg}`,
  borderRadius: 6,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.72rem",
  cursor: "pointer",
  padding: 0,
});

// ── Main page ────────────────────────────────────────────────────────────────
const PageImagesPage: React.FC = () => {
  const { data: summaries = [] } = useFetchPageSummariesQuery();
  const [active, setActive] = useState<PageName>("Residential");

  const activePage = PAGES.find((p) => p.name === active)!;
  const summary    = summaries.find((s) => s.page === active);

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#134e4a,#0d9488,#14b8a6)", padding: "12px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-images" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#fff" }}>Page Images Manager</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.72)" }}>
                  Manage images independently for each website page — upload, reorder, and delete without affecting other pages
                </div>
              </div>
            </div>
          </div>

          {/* Page summary cards — grouped */}
          {(["Projects", "Materials"] as const).map((grp) => (
            <div key={grp} style={{ padding: grp === "Projects" ? "16px 22px 0" : "12px 22px 0" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#94a3b8", marginBottom: 8 }}>
                {grp === "Projects" ? "Project Pages" : "Home Page — Materials"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
                {PAGES.filter((p) => p.group === grp).map((pg) => {
              const s   = summaries.find((x) => x.page === pg.name);
              const sel = active === pg.name;
              return (
                <button
                  key={pg.name}
                  type="button"
                  onClick={() => setActive(pg.name)}
                  style={{
                    background: sel ? pg.accent : "#f8fafc",
                    border: `2px solid ${sel ? pg.accent : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "10px 14px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <i className={`bi ${pg.icon}`} style={{ color: sel ? "#fff" : pg.accent, fontSize: "0.9rem" }} />
                    <span style={{ fontWeight: 700, fontSize: "0.82rem", color: sel ? "#fff" : "#334155" }}>{pg.label}</span>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: sel ? "rgba(255,255,255,0.8)" : "#64748b" }}>
                    {s?.imageCount ?? 0} image{(s?.imageCount ?? 0) !== 1 ? "s" : ""}
                  </div>
                  {s?.thumbnailUrl && (
                    <div style={{ marginTop: 6, width: "100%", height: 52, borderRadius: 6, overflow: "hidden" }}>
                      <img src={s.thumbnailUrl} alt={pg.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                </button>
              );
            })}
              </div>
            </div>
          ))}

          {/* Active page content */}
          <div style={{ padding: "20px 22px 24px" }}>
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: activePage.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`bi ${activePage.icon}`} style={{ color: "#fff", fontSize: "0.78rem" }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f172a" }}>{activePage.label} Page</div>
                <div style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{activePage.desc}</div>
              </div>
              <div style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#64748b", background: "#f1f5f9", padding: "4px 12px", borderRadius: 20 }}>
                {summary?.imageCount ?? 0} image{(summary?.imageCount ?? 0) !== 1 ? "s" : ""}
              </div>
            </div>

            <PageTab key={active} page={active} accent={activePage.accent} />
          </div>

        </div>
      </div>
    </section>
  );
};

export default PageImagesPage;
